'use client';

import type { CtaType } from '@ria-client/segment/Analytics/types';
import { LinkType, MonetizableEnum } from '@ria-client/segment/enums';
import type { AnalyticsType } from '@ria-client/segment/types';
import { EntityPlacementCtaTypeEnum } from '@ria-client/supergraph/marketplace/enums';
import { Button } from '@ria-client/tw-shadcn/components/button';
import { cn } from 'cn';
import { ExternalLink } from 'lucide-react';
import { TrackedLink } from './TrackedLink';

export type CtaButtonVariant = 'primary' | 'outlinePrimary';

export interface CtaButtonProps {
  /** Absent when the content layer has no offer to link to; the CTA renders nothing. */
  cta?: CtaType;
  analyticsPayload: AnalyticsType;
  variant?: CtaButtonVariant;
  /** `dense` is the treatment the financial-advisors card and table use. */
  size?: 'default' | 'dense';
  fullWidth?: boolean;
  hideSubtext?: boolean;
  federallyInsuredAnnotation?: string;
  className?: string;
}

/*
 * Geometry from packages/ui/src/CtaButton/index.tsx:106-119. The primitive
 * already supplies the uppercase 13px/1.2/0.4px typography and the full-width
 * default, so only the CTA-specific height, padding and tracking are restated.
 */
const CTA_GEOMETRY = 'h-[45px] whitespace-nowrap px-4 py-2 leading-none tracking-[0.8125px] md:p-4';
const DENSE = 'text-[14px] tracking-[1px]';

/**
 * The financial-advisors call to action: a tracked anchor styled as a button,
 * with optional subtext and the accessible context a link leaving NerdWallet needs.
 *
 * The RIA disclaimer and the external-site annotation text stay with the
 * composites that own that copy; this component owns only CTA behavior.
 */
export function CtaButton({
  cta,
  analyticsPayload,
  variant = 'primary',
  size = 'default',
  fullWidth = false,
  hideSubtext = false,
  federallyInsuredAnnotation,
  className,
}: CtaButtonProps): React.JSX.Element | null {
  if (!cta) {
    return null;
  }

  /*
   * Matches packages/ui/src/CtaButton/index.tsx:123-129: the partner-site context
   * shows for a monetizing offer or an explicit apply-now CTA, and deliberately
   * ignores cta.showIcon, which the legacy component also ignores.
   */
  const leavesNerdWallet =
    analyticsPayload.monetizable === MonetizableEnum.yesAssumed ||
    cta.type === EntityPlacementCtaTypeEnum.ApplyNow;
  const ctaSubtext =
    cta.subtext && federallyInsuredAnnotation
      ? `${cta.subtext}, ${federallyInsuredAnnotation}`
      : cta.subtext;

  return (
    <div
      data-slot="cta"
      className={cn('flex flex-col gap-2 text-center', fullWidth ? 'w-full' : 'w-auto')}
    >
      <Button
        asChild
        // secondary and outlineSecondary are absent on purpose: variants.ts
        // declares them, no call site uses them, and the epic forbids shipping a
        // variant without a consumer.
        variant={variant === 'outlinePrimary' ? 'outline' : 'default'}
        className={cn(CTA_GEOMETRY, size === 'dense' && DENSE, className)}
      >
        <TrackedLink
          cta={cta}
          analyticsPayload={analyticsPayload}
          monetizable={analyticsPayload.monetizable}
          linkType={LinkType.APPLY_NOW_BUTTON}
        >
          {cta.text}
          {leavesNerdWallet && (
            <>
              <span className="sr-only">on partner&apos;s site</span>
              <ExternalLink aria-hidden="true" className="ml-1" />
            </>
          )}
        </TrackedLink>
      </Button>
      {ctaSubtext && !hideSubtext && (
        <span
          data-slot="cta-subtext"
          className="typography-text-small whitespace-normal text-center text-muted-foreground"
        >
          {ctaSubtext}
        </span>
      )}
    </div>
  );
}
